import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, Users, DollarSign, Ticket, Radio, Download, Copy, Droplets } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

export default function Admin() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [signups, setSignups] = useState([]);
  const [signupsLink, setSignupsLink] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, u, c, su, sl] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/stream-config"),
        api.get("/admin/signups"),
        api.get("/admin/signups-link"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setCfg(c.data);
      setSignups(su.data);
      setSignupsLink(sl.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load admin");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveCfg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/admin/stream-config", cfg);
      setCfg(r.data);
      toast.success("Stream config saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied to clipboard`),
      () => toast.error("Copy failed — please select and copy manually"),
    );
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/signups.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "corporate-clash-signups.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };

  if (!stats || !cfg) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505] text-white">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-logo">
            <Logo size={32} />
            <span className="sr-only">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/watch" className="btn-ghost px-4 py-2 text-xs" data-testid="admin-watch-link">
              Watch
            </Link>
            <button onClick={logout} className="btn-ghost px-4 py-2 text-xs" data-testid="admin-logout-btn">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 space-y-12">
        <section>
          <p className="eyebrow text-rose-500">Control Room</p>
          <h1 className="font-display text-6xl mt-3 uppercase">Event Dashboard</h1>
          <p className="text-zinc-400 mt-3">
            Signed in as <strong>{user?.email}</strong>
          </p>
        </section>

        {/* Stats */}
        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10" data-testid="admin-stats-grid">
          <StatCard icon={Users} label="Registered" value={stats.total_users} />
          <StatCard icon={Ticket} label="Tickets Sold" value={stats.paid_users} />
          <StatCard icon={Radio} label="Captured Orders" value={stats.orders_completed} />
          <StatCard
            icon={DollarSign}
            label="Est. Revenue"
            value={`$${stats.estimated_revenue_usd}`}
            accent
          />
        </section>

        {/* Stream config */}
        <section className="card-flat p-8" data-testid="admin-stream-form">
          <p className="eyebrow text-zinc-500">Live Broadcast Settings</p>
          <h2 className="font-display text-4xl mt-3 uppercase">Stream Configuration</h2>
          <form onSubmit={saveCfg} className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="eyebrow block mb-2">Embed URL</label>
              <input
                className="flat"
                value={cfg.stream_embed_url}
                onChange={(e) => setCfg({ ...cfg, stream_embed_url: e.target.value })}
                placeholder="https://www.youtube.com/embed/XXXX"
                data-testid="admin-stream-url-input"
              />
              <p className="mt-2 text-xs text-zinc-500">
                Paste an embed URL (YouTube unlisted /embed/, Vimeo /video/embed/,
                Cloudflare iframe src, etc.). This is never exposed to non-paying users.
              </p>
            </div>
            <div>
              <label className="eyebrow block mb-2">Event Title</label>
              <input
                className="flat"
                value={cfg.event_title}
                onChange={(e) => setCfg({ ...cfg, event_title: e.target.value })}
                data-testid="admin-event-title-input"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Subtitle</label>
              <input
                className="flat"
                value={cfg.event_subtitle}
                onChange={(e) => setCfg({ ...cfg, event_subtitle: e.target.value })}
                data-testid="admin-event-subtitle-input"
              />
            </div>
            <div className="md:col-span-2 flex items-center justify-between border border-white/10 p-5">
              <div className="flex items-center gap-3">
                <Droplets size={18} className="text-rose-500" />
                <div>
                  <p className="eyebrow text-zinc-500">Email Watermark</p>
                  <p className="mt-1 text-sm text-zinc-300">
                    {cfg.watermark_enabled
                      ? "On — viewer email tiled across the stream."
                      : "Off — clean stream, no overlay."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCfg({ ...cfg, watermark_enabled: !cfg.watermark_enabled })}
                className={`px-6 py-3 text-xs uppercase tracking-widest font-bold ${
                  cfg.watermark_enabled ? "bg-rose-600 text-white" : "btn-ghost"
                }`}
                data-testid="admin-toggle-watermark-btn"
              >
                {cfg.watermark_enabled ? "Disable" : "Enable"}
              </button>
            </div>
            <div className="md:col-span-2 flex items-center justify-between border border-white/10 p-5">
              <div>
                <p className="eyebrow text-zinc-500">Broadcast Status</p>
                <p className="mt-1 font-display text-3xl">
                  {cfg.is_live ? "LIVE ON AIR" : "OFF AIR"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCfg({ ...cfg, is_live: !cfg.is_live })}
                className={`px-6 py-3 text-xs uppercase tracking-widest font-bold ${
                  cfg.is_live ? "bg-rose-600 text-white" : "btn-ghost"
                }`}
                data-testid="admin-toggle-live-btn"
              >
                {cfg.is_live ? "Take Off Air" : "Go Live"}
              </button>
            </div>
            <div className="md:col-span-2">
              <button
                disabled={saving}
                className="btn-primary px-8 py-4 text-sm flex items-center gap-2 disabled:opacity-60"
                data-testid="admin-save-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save Configuration
              </button>
            </div>
          </form>
        </section>

        {/* Next-year signups (QR funnel) */}
        <section data-testid="admin-signups-section">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <p className="eyebrow text-rose-500">QR Funnel</p>
              <h2 className="font-display text-4xl uppercase">Next Year Sign-ups</h2>
              <p className="mt-2 text-zinc-400 text-sm">
                {signups.length} {signups.length === 1 ? "person" : "people"} have scanned the QR and submitted details.
              </p>
            </div>
            <button
              onClick={downloadCsv}
              className="btn-ghost px-5 py-3 text-xs flex items-center gap-2"
              data-testid="admin-download-signups-csv"
            >
              <Download size={14} /> Download CSV
            </button>
          </div>

          {signupsLink?.configured && (
            <div className="card-flat p-6 mb-6" data-testid="admin-sheets-sync">
              <p className="eyebrow text-zinc-500">Live Google Sheets sync</p>
              <p className="mt-3 text-sm text-zinc-300">
                Paste this into cell <code className="bg-black/40 px-1.5 py-0.5">A1</code> of a blank Google Sheet.
                Sheets refreshes <code className="bg-black/40 px-1.5 py-0.5">IMPORTDATA</code> roughly every hour
                so the spreadsheet stays up to date as new people sign up.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row items-stretch gap-3">
                <code className="flex-1 bg-black/60 border border-white/10 p-3 text-xs text-zinc-300 break-all">
                  {signupsLink.formula}
                </code>
                <button
                  onClick={() => copyToClipboard(signupsLink.formula, "Sheets formula")}
                  className="btn-primary px-4 py-3 text-xs flex items-center gap-2 justify-center"
                  data-testid="admin-copy-sheets-formula"
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
              <p className="mt-3 text-xs text-zinc-500">
                Keep the token secret — anyone with this URL can read the raw signup list.
              </p>
            </div>
          )}

          <div className="card-flat overflow-x-auto" data-testid="admin-signups-table">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-zinc-400 uppercase tracking-widest text-xs">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Company</th>
                  <th className="text-left p-3">Position</th>
                  <th className="text-left p-3">Age</th>
                  <th className="text-left p-3">Weight</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Phone</th>
                </tr>
              </thead>
              <tbody>
                {signups.map((s) => (
                  <tr key={s.id} className="border-t border-white/5">
                    <td className="p-3 text-zinc-500 whitespace-nowrap">
                      {new Date(s.created_at).toLocaleString()}
                    </td>
                    <td className="p-3">{s.first_name} {s.last_name}</td>
                    <td className="p-3">{s.company}</td>
                    <td className="p-3">{s.position}</td>
                    <td className="p-3">{s.age}</td>
                    <td className="p-3">{s.weight_kg} kg</td>
                    <td className="p-3 break-all">{s.email}</td>
                    <td className="p-3 whitespace-nowrap">{s.phone}</td>
                  </tr>
                ))}
                {!signups.length && (
                  <tr><td className="p-6 text-zinc-500" colSpan={8}>No sign-ups yet. Share the QR during the broadcast.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Users */}
        <section data-testid="admin-users-table">
          <h2 className="font-display text-4xl uppercase">Ticket Holders</h2>
          <div className="mt-6 card-flat overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-zinc-400 uppercase tracking-widest text-xs">
                <tr>
                  <th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Paid</th>
                  <th className="text-left p-4">Paid At</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-white/5">
                    <td className="p-4 break-all">{u.email}</td>
                    <td className="p-4">{u.full_name}</td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 text-xs uppercase tracking-widest ${
                          u.paid ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/10 text-zinc-500"
                        }`}
                      >
                        {u.paid ? "Paid" : "Unpaid"}
                      </span>
                    </td>
                    <td className="p-4 text-zinc-500">
                      {u.paid_at ? new Date(u.paid_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
                {!users.length && (
                  <tr><td className="p-6 text-zinc-500" colSpan={4}>No users yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-6 ${accent ? "bg-rose-600" : "bg-[#0a0a0a]"}`}>
      <Icon size={20} className={accent ? "text-white" : "text-rose-500"} />
      <p className={`eyebrow mt-4 ${accent ? "text-white/80" : "text-zinc-500"}`}>{label}</p>
      <p className="font-display text-5xl mt-2 leading-none">{value}</p>
    </div>
  );
}
